/**
 * THE PAGE THAT DID NOT EXIST.
 *
 * `AppRouter`'s <Routes> had no `path="*"`, so any URL that matched nothing —
 * a typo, a stale bookmark, a link from an old email, an /invite with its token
 * lost — rendered NOTHING. Not a message, not a redirect: an empty porcelain
 * screen carrying only the floating notification bell and the IQ launcher, on a
 * product that had otherwise loaded correctly. The only signal that anything was
 * wrong was the absence of everything.
 *
 * WHY IT IS NOT A REDIRECT. Silently bouncing to the home screen is the pattern
 * this audit found everywhere else in the shell and it teaches the same wrong
 * lesson: the user pressed something, arrived somewhere they did not choose, and
 * was told nothing. A person who mistyped a URL needs to know it was the URL.
 *
 * The way home is offered rather than taken, and it is resolved per user —
 * a concierge's home is the desk, an owner's is the dashboard, a resident's is
 * the portal — because "go to /dashboard" is a dead end for two of those three.
 */
import React from 'react';
import styled from '@emotion/styled';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/auth-provider';
import { resolvePostAuthRoute } from '../../../utils/post-auth-route';
import { BackButton } from '../../layout/back-button';
import { Text, font } from '../../../style-utils/text';
import { palette, spacing, typography } from '../../../style-utils/theme';

const Page = styled.main`
  max-width: 520px;
  margin: 0 auto;
  padding: ${spacing.xxl} ${spacing.md};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.sm};
`;

const Path = styled.code`
  display: block;
  max-width: 100%;
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: 8px;
  background: ${palette.surfaceMuted};
  color: ${palette.ink};
  /* A <code> element with no family falls back to the browser's own monospace —
     source-code-pro here — which is the one place in the product rendering a
     face that is not in the type system. JetBrains Mono is the token for exactly
     this: codes, tokens and log ids. */
  font-family: ${typography.mono};
  font-size: ${typography.size.xs}px;
  overflow-wrap: anywhere;
`;

export const NotFound: React.FC = () => {
  const { pathname } = useLocation();
  const { user, onboarding, permissions } = useAuth();
  const home = resolvePostAuthRoute(user, onboarding, permissions || []);

  return (
    /* THE SKIP LINK IS MOUNTED GLOBALLY AND POINTED AT NOTHING HERE. This
       page has its own <main> and never carried `id="main-content"`, so the
       first control in the tab order moved the URL fragment and left focus
       where it was — measured on /no-such-page-exists. tabIndex -1 is what
       lets a heading-less region actually receive that jump. */
    <Page id="main-content" tabIndex={-1}>
      <Text as="h1" fontSize={font.size.xl} fontWeight={700} mt={0} mb={0}>
        That page is not here
      </Text>
      <Text fontSize={14} mt={0} mb={0} color={palette.slate}>
        Nothing in Lobbero answers to this address. It may have been mistyped, or
        it may be a link that has since moved.
      </Text>
      {/* The address is SHOWN. A person who followed a link from an email needs
          to be able to see what the link actually said before they can report
          it or correct it. */}
      <Path>{pathname}</Path>
      <BackButton to={home} label="Go to your home screen" />
    </Page>
  );
};
